import React, { useEffect, useState, createContext, useContext } from 'react';
// Define the Avatar type
export type Avatar = {
  id: string;
  name: string;
  description: string;
  image: string;
  accent: string;
  secondaryAccent: string;
  particleType: string;
  cursorStyle: string;
  systemMessage: string;
  locked?: boolean;
};
// Celebrity avatars data with muted ethereal colors
const defaultAvatars: Avatar[] = [{
  id: 'zendaya',
  name: 'ZENDAYA',
  description: 'Award-winning actress and fashion icon known for her versatile performances and cultural impact.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xqBZaw44CFzLGjczomo1EJ/Zendaya.jpg',
  accent: '#a0e0e8',
  secondaryAccent: '#7badc7',
  particleType: 'celestial',
  cursorStyle: 'celestial',
  systemMessage: 'ZENDAYA INTERFACE CONNECTED'
}, {
  id: 'michael',
  name: 'MICHAEL B. JORDAN',
  description: 'Versatile actor and producer with commanding screen presence and cultural significance.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/kaM3qRE9FopcncPMrcD29L/Image_1.jpg',
  accent: '#a9d6c2',
  secondaryAccent: '#8bc7c5',
  particleType: 'nexus',
  cursorStyle: 'nexus',
  systemMessage: 'MICHAEL CONNECTION ESTABLISHED'
}, {
  id: 'keanu',
  name: 'KEANU REEVES',
  description: 'Legendary actor with enigmatic presence and profound cultural impact across generations.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cP7TZ1RhT7dstjWsgB6XqQ/Keanu_Reeves.webp',
  accent: '#a0a0a0',
  secondaryAccent: '#707070',
  particleType: 'enigma',
  cursorStyle: 'enigma',
  systemMessage: 'KEANU PROTOCOLS BREACHED',
  locked: true
}];
// Define the context type
type AvatarContextType = {
  avatar: Avatar | null;
  setAvatar: (avatar: Avatar) => void;
  showAvatarSelection: boolean;
  setShowAvatarSelection: (show: boolean) => void;
  resetAvatar: () => void;
  allAvatars: Avatar[];
};
// Create the context
const AvatarContext = createContext<AvatarContextType | undefined>(undefined);
// Provider component
export const AvatarProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [avatar, setAvatarState] = useState<Avatar | null>(null);
  const [showAvatarSelection, setShowAvatarSelection] = useState(true);
  const [allAvatars] = useState<Avatar[]>(defaultAvatars);
  // Set avatar and apply theme
  const setAvatar = (selectedAvatar: Avatar) => {
    setAvatarState(selectedAvatar);
    // Apply CSS variables
    document.documentElement.style.setProperty('--primary', selectedAvatar.accent);
    document.documentElement.style.setProperty('--primary-dark', selectedAvatar.secondaryAccent);
    document.documentElement.style.setProperty('--secondary', '#d3b2d8');
    // Store in localStorage
    localStorage.setItem('selectedAvatar', selectedAvatar.id);
  };
  // Reset avatar selection
  const resetAvatar = () => {
    localStorage.removeItem('selectedAvatar');
    setAvatarState(null);
    setShowAvatarSelection(true);
    // Reset CSS variables to defaults
    document.documentElement.style.setProperty('--primary', '#a0e0e8');
    document.documentElement.style.setProperty('--primary-dark', '#7badc7');
    document.documentElement.style.setProperty('--secondary', '#d3b2d8');
  };
  // Check for saved avatar on initial load
  useEffect(() => {
    const savedAvatarId = localStorage.getItem('selectedAvatar');
    if (savedAvatarId) {
      const savedAvatar = allAvatars.find(a => a.id === savedAvatarId);
      if (savedAvatar) {
        setAvatar(savedAvatar);
        setShowAvatarSelection(false);
      }
    }
  }, [allAvatars]);
  return <AvatarContext.Provider value={{
    avatar,
    setAvatar,
    showAvatarSelection,
    setShowAvatarSelection,
    resetAvatar,
    allAvatars
  }} data-id="element-1377">
      {children}
    </AvatarContext.Provider>;
};
// Custom hook to use the avatar context
export const useAvatar = () => {
  const context = useContext(AvatarContext);
  if (context === undefined) {
    throw new Error('useAvatar must be used within an AvatarProvider');
  }
  return context;
};